import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Integration } from "@/lib/types";

export function useIntegrations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["integrations", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("integrations")
        .select("*")
        .eq("user_id", user.id)
        .order("type");
      if (error) throw error;
      return data as Integration[];
    },
    enabled: !!user,
  });
}

export function useUpsertIntegration() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      type: string;
      config: Record<string, unknown>;
      enabled?: boolean;
    }) => {
      if (!user) throw new Error("Not authenticated");

      // Check if integration already exists
      const { data: existing } = await supabase
        .from("integrations")
        .select("id")
        .eq("user_id", user.id)
        .eq("type", input.type)
        .single();

      if (existing) {
        const { data, error } = await supabase
          .from("integrations")
          .update({ config: input.config, enabled: input.enabled ?? true })
          .eq("id", existing.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from("integrations")
          .insert({
            user_id: user.id,
            type: input.type,
            config: input.config,
            enabled: input.enabled ?? true,
          })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useSyncIntegration() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (type: string) => {
      if (!user) throw new Error("Not authenticated");
      // Gmail sync uses the new function name
      const functionName = type === "gmail" ? "gmail-sync" : `sync-${type}`;
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { user_id: user.id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({ queryKey: ["emails"] });
    },
  });
}

export function useUpdateIntegrationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; configPatch: Record<string, unknown> }) => {
      const { data: existing, error: fetchError } = await supabase
        .from("integrations")
        .select("config")
        .eq("id", input.id)
        .single();
      if (fetchError) throw fetchError;
      const merged = { ...((existing?.config as Record<string, unknown>) || {}), ...input.configPatch };
      const { error } = await supabase
        .from("integrations")
        .update({ config: merged })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("integrations")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({ queryKey: ["emails"] });
    },
  });
}
